import type { StudyCard } from './study.ts';

const nativeStudyKeyboardOwnerSelector =
  'button, a[href], input, textarea, select';

export type StudyOptionKeyboardAction = 'submit' | 'native';

export type StudyKeyboardEvent = {
  altKey: boolean;
  ctrlKey: boolean;
  key: string;
  metaKey: boolean;
  shiftKey: boolean;
};

export function isUnmodifiedStudyKey(event: StudyKeyboardEvent): boolean {
  return !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey;
}

type ClosestTarget = {
  closest: (selector: string) => unknown;
  getAttribute?: (name: string) => string | null;
};

export function isStudyKeyboardOwnedByNativeControl(
  target: EventTarget | ClosestTarget | null,
): boolean {
  if (typeof target !== 'object' || target === null) return false;
  const element = target as Partial<ClosestTarget>;
  if (typeof element.closest !== 'function') return false;
  if (element.closest(nativeStudyKeyboardOwnerSelector)) return true;
  const editable = element.closest('[contenteditable]');
  if (!editable) return false;
  const getAttribute = (editable as Partial<ClosestTarget>).getAttribute;
  if (typeof getAttribute !== 'function') return true;
  return getAttribute.call(editable, 'contenteditable') !== 'false';
}

export function optionIndexForShortcut(
  key: string,
  optionCount: number,
): number | null {
  if (!/^\d$/.test(key)) return null;
  const index = Number(key) - 1;
  return index >= 0 && index < optionCount ? index : null;
}

export function toggleStudyOption(
  type: 'choice' | 'multi-choice',
  selectedIds: readonly string[],
  optionId: string,
): string[] {
  if (type === 'choice') return [optionId];
  return selectedIds.includes(optionId)
    ? selectedIds.filter((id) => id !== optionId)
    : [...selectedIds, optionId];
}

/** Keep native activation for selection, but submit an option already selected. */
export function studyOptionKeyboardAction(
  event: StudyKeyboardEvent,
  isSelected: boolean,
): StudyOptionKeyboardAction {
  return isUnmodifiedStudyKey(event) && event.key === 'Enter' && isSelected
    ? 'submit'
    : 'native';
}

export function selectStudyOptionFromShortcut(
  type: 'choice' | 'multi-choice',
  selectedIds: readonly string[],
  optionId: string,
): { selectedOptionIds: string[]; submitImmediately: boolean } {
  return {
    selectedOptionIds: toggleStudyOption(type, selectedIds, optionId),
    submitImmediately: type === 'choice',
  };
}

export function canSubmitStudyAnswer(
  card: StudyCard,
  textValue: string,
  selectedOptionIds: readonly string[],
  orderWorkspaceLength = 0,
): boolean {
  if (card.type === 'text-input') return textValue.trim().length > 0;
  if (card.type === 'order-words')
    return orderWorkspaceLength === card.items.length;
  return selectedOptionIds.length > 0;
}
